/**
 * Bulk save and send handler.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback } from 'react';
import type { ShortlinkClickIdStructure, ShortlinkDomainScheme } from '@trafficpulse/shared';
import { formatSendErrorDescription, getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { applyScheduleInterval } from '@/features/smswiser-campaigns/utils/scheduleInterval';
import {
  bulkUpdateNeedsPerItemCreative,
  stripPerDropCreativeFields,
  withPerDropRowApplied,
} from '@/features/smswiser-campaigns/utils/bulkUpdatePerDropCreative';
import { groupItemsByCountryKey } from '@/features/smswiser-campaigns/utils/preparedDropBulkByCountry';

export interface BulkSaveAndSendPayload {
  routeId?: number;
  shortLinkTldValue?: string;
  amount?: number;
  leadsListWiserId?: number;
  wiserId?: number;
  executeAfter?: string | null;
  senderNames?: string[];
  smsTexts?: string[];
  smsTextsPerDrop?: string[][];
  senderNamesPerDrop?: string[][];
  clickIdStructure?: ShortlinkClickIdStructure;
  domainScheme?: ShortlinkDomainScheme;
}

export interface UseBulkSaveAndSendParams {
  drops: PreparedDrop[];
  prepared: { refetch: () => void | Promise<unknown> };
  bulkUpdatePreparedFlex: {
    mutateAsync: (params: {
      countryKey: string;
      items: Array<{ id: string; wiserId: number }>;
      payload: BulkSaveAndSendPayload;
    }) => Promise<unknown>;
  };
  handleBulkSend: (
    ids: string[],
    onProgress?: (sent: number, failed: number) => void,
  ) => Promise<{ sent: number; failed: number; errorMessages?: string[] }>;
  handleBulkSendComplete: (result: { sent: number; failed: number; errorMessages?: string[] }) => void;
}

export function useBulkSaveAndSend({
  drops,
  prepared,
  bulkUpdatePreparedFlex,
  handleBulkSend,
  handleBulkSendComplete,
}: UseBulkSaveAndSendParams) {
  const { loading: toastLoading, update: toastUpdate, dismiss: toastDismiss, error: toastError } = useToast();

  return useCallback(
    async (ids: string[], payload: BulkSaveAndSendPayload, scheduleIntervalMinutes?: number) => {
      const items = ids
        .map((id) => drops.find((d) => d.id === id))
        .filter((d): d is NonNullable<typeof d> => Boolean(d))
        .map((d) => ({ id: d.id, wiserId: d.wiserId }));
      if (items.length === 0) return;

      const loadingId = toastLoading('Saving drafts...', 'Applying changes.');
      try {
        const basePayload = stripPerDropCreativeFields(payload) as BulkSaveAndSendPayload;
        const expandCreative = bulkUpdateNeedsPerItemCreative(items.length, payload);
        const perDropTexts = payload.smsTextsPerDrop;
        const perDropSenders = payload.senderNamesPerDrop;
        const needsSchedulePerItem =
          Boolean(scheduleIntervalMinutes && scheduleIntervalMinutes > 0 && payload.executeAfter);
        const needsPerItem = needsSchedulePerItem || expandCreative;

        if (needsPerItem) {
          for (let i = 0; i < items.length; i++) {
            let perItemPayload: BulkSaveAndSendPayload = { ...basePayload };
            if (needsSchedulePerItem && payload.executeAfter) {
              perItemPayload.executeAfter = applyScheduleInterval(payload.executeAfter, scheduleIntervalMinutes!, i);
            }
            if (expandCreative) {
              perItemPayload = withPerDropRowApplied(perItemPayload, i, perDropTexts, perDropSenders);
            }
            const one = items[i]!;
            const d = drops.find((x) => x.id === one.id);
            if (!d?.countryKey) continue;
            await bulkUpdatePreparedFlex.mutateAsync({
              countryKey: d.countryKey,
              items: [one],
              payload: perItemPayload,
            });
          }
        } else {
          const byCountry = groupItemsByCountryKey(items, drops);
          for (const [ck, groupItems] of byCountry) {
            await bulkUpdatePreparedFlex.mutateAsync({
              countryKey: ck,
              items: groupItems,
              payload: basePayload,
            });
          }
        }
        await prepared.refetch();
        toastUpdate(loadingId, 'Sending drafts...', `0 of ${ids.length} sent`);
        const result = await handleBulkSend(ids, (sent, failed) => {
          toastUpdate(loadingId, 'Sending drafts...', `${sent} of ${ids.length} sent${failed > 0 ? `, ${failed} failed` : ''}`);
        });
        toastDismiss(loadingId);
        if (result.failed > 0) {
          toastError(
            'Bulk send finished with errors',
            formatSendErrorDescription({
              sent: result.sent,
              failed: result.failed,
              errorMessages: result.errorMessages,
            }),
          );
        }
        handleBulkSendComplete(result);
      } catch (err) {
        toastDismiss(loadingId);
        toastError('Save and send failed', getApiErrorMessage(err, 'Could not save and send'));
        throw err;
      }
    },
    [
      drops,
      prepared,
      bulkUpdatePreparedFlex,
      handleBulkSend,
      handleBulkSendComplete,
      toastLoading,
      toastUpdate,
      toastDismiss,
      toastError,
    ],
  );
}
